import { useMemo } from 'react'
import { buildChapterOptions } from '../utils/chapterOptions'

const asId = value => (value == null ? '' : String(value))
const asName = (item, fallbackKeys) => {
  for (const key of fallbackKeys) {
    if (item?.[key]) return String(item[key])
  }
  return asId(item?.id)
}

/** Raw API instruments and formatted instrument-list rows both appear in redux. */
/** Redux cohortList rows are SortableTable cells; API payloads use study_group_id / group_name. */
export const normalizeTrackOption = track => {
  if (!track) return null
  if (Array.isArray(track)) {
    const idCell = track.find(cell => cell.key === 'id')
    const nameCell = track.find(cell => cell.key === 'name')
    const id = asId(idCell?.value)
    if (!id) return null
    const name = nameCell?.value ? String(nameCell.value) : id
    return { id, name }
  }
  const id = asId(track.id || track.study_group_id)
  if (!id) return null
  return {
    id,
    name: track.name || track.group_name || id,
  }
}

const trackOptionsFromSources = (tracks, cohortMap) => {
  const fromTracks = (tracks || []).map(normalizeTrackOption).filter(Boolean)
  if (fromTracks.length) return fromTracks
  return Object.entries(cohortMap || {})
    .map(([id, name]) => ({ id: asId(id), name: String(name || id) }))
    .filter(item => item.id)
}

const cellValue = (row, keys) => {
  if (!Array.isArray(row)) return ''
  for (const key of keys) {
    const cell = row.find(item => item.key === key)
    if (cell?.value != null && cell.value !== '') return cell.value
  }
  return ''
}

/** siteSelectionList / sitesList rows use SortableTable cells; API sites use id + label. */
export const normalizeSiteOption = (site, sitesMap = {}) => {
  if (!site) return null
  if (Array.isArray(site)) {
    const id = asId(cellValue(site, ['id', 'siteId']))
    if (!id) return null
    const name = cellValue(site, ['name', 'siteName'])
    return { id, name: name ? String(name) : String(sitesMap[id] || id) }
  }
  if (typeof site === 'string' || typeof site === 'number') {
    const id = asId(site)
    if (!id) return null
    return { id, name: String(sitesMap[id] || id) }
  }
  const id = asId(site.id || site.site_id)
  if (!id) return null
  return {
    id,
    name: site.label || site.name || site.site_name || String(sitesMap[id] || id),
  }
}

/** Audience Site-Location picker: leaf sites only (matches deployment site selection). */
export const selectLeafSitesForAudience = (sitesState = {}) => {
  const leafFromApi = (sitesState.allSites || []).filter(site => site?.is_leaf)
  if (leafFromApi.length) return leafFromApi
  const sitesMap = sitesState.sitesMap || {}
  return (sitesState.leafSites || [])
    .map(id => ({ id, label: sitesMap[id] || String(id) }))
    .filter(site => site.id != null && site.id !== '')
}

const siteOptionsFromSources = (sites, sitesMap) => {
  const fromSites = (sites || []).map(site => normalizeSiteOption(site, sitesMap)).filter(Boolean)
  if (fromSites.length) return fromSites
  return Object.entries(sitesMap || {})
    .map(([id, name]) => ({ id: asId(id), name: String(name || id) }))
    .filter(item => item.id)
}

const instrumentTitle = item => {
  if (!item) return ''
  if (item.payload?.title) return String(item.payload.title)
  if (item.title) return String(item.title)
  if (item.metadata?.display_name) return String(item.metadata.display_name)
  if (item.name) return String(item.name)
  if (Array.isArray(item)) {
    const nameCell = item.find(cell => cell?.key === 'name')
    if (nameCell?.value) return String(nameCell.value)
  }
  return asId(item.id)
}

const mapVisitTree = arms =>
  (arms || []).map(arm => ({
    id: asId(arm.id || arm.arm_id),
    name: asName(arm, ['name', 'arm_name', 'label']),
    phases: (arm.phases || []).map(phase => ({
      id: asId(phase.id || phase.phase_id),
      name: asName(phase, ['name', 'phase_name', 'label']),
      cycles: (phase.cycles || []).map(cycle => ({
        id: asId(cycle.id || cycle.cycle_id),
        name: asName(cycle, ['name', 'cycle_name', 'label']),
        is_dummy: cycle.is_dummy,
        isDummy: cycle.isDummy,
        metadata: cycle.metadata,
        visits: (cycle.visits || []).map(visit => ({
          id: asId(visit.id || visit.visit_id),
          name: asName(visit, ['name', 'visit_name', 'label']),
        })),
      })),
    })),
  }))

const flatten = (arms, pick) => {
  const list = []
  mapVisitTree(arms).forEach(arm => {
    arm.phases.forEach(phase => {
      phase.cycles.forEach(cycle => {
        cycle.visits.forEach(visit => list.push(pick({ arm, phase, cycle, visit })))
      })
    })
  })
  return list
}

export const useEntityOptions = ({
  visitTemplates = [],
  studyVisitSchedule = [],
  instruments = [],
  sites = [],
  sitesMap = {},
  tracks = [],
  cohortMap = {},
  study = {},
  currentInstrumentId,
} = {}) =>
  useMemo(() => {
    const arms = mapVisitTree(studyVisitSchedule?.length ? studyVisitSchedule : visitTemplates)
    const phases = arms.flatMap(arm => arm.phases.map(phase => ({ ...phase, armId: arm.id })))
    const cycles = arms.flatMap(arm =>
      arm.phases.flatMap(phase => phase.cycles.map(cycle => ({ ...cycle, phaseId: phase.id, armId: arm.id }))),
    )
    const visits = flatten(studyVisitSchedule?.length ? studyVisitSchedule : visitTemplates, ({ visit, cycle, phase, arm }) => ({
      ...visit,
      cycleId: cycle.id,
      phaseId: phase.id,
      armId: arm.id,
    }))
    const chapters = buildChapterOptions(study)
    const instrumentOptions = (instruments || [])
      .filter(item => String(item.id) !== String(currentInstrumentId))
      .map(item => ({ id: asId(item.id), name: instrumentTitle(item) }))
    const siteOptions = siteOptionsFromSources(sites, sitesMap)
    const trackOptions = trackOptionsFromSources(tracks, cohortMap)
    return {
      arms,
      phases,
      cycles,
      visits,
      chapters,
      instruments: instrumentOptions,
      sites: siteOptions,
      tracks: trackOptions,
      languages: [
        { id: 'en', name: 'English' },
        { id: 'es', name: 'Spanish' },
        { id: 'fr', name: 'French' },
        { id: 'de', name: 'German' },
      ],
    }
  }, [visitTemplates, studyVisitSchedule, instruments, sites, sitesMap, tracks, cohortMap, study, currentInstrumentId])
